import React, { useEffect, useRef } from 'react';

const styles = `
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes slideIn {
    from { opacity: 0; transform: translateX(-5px); }
    to   { opacity: 1; transform: translateX(0); }
  }
`;

const SEV_STYLES = {
  Critical:   { bg: '#BC2B3020', color: '#BC2B30',  border: '#BC2B3040' },
  High:       { bg: '#ef444420', color: '#ef4444',  border: '#ef444440' },
  Medium:     { bg: '#f59e0b20', color: '#f59e0b',  border: '#f59e0b40' },
  Low:        { bg: '#10b98120', color: '#10b981',  border: '#10b98140' },
  Compliant:  { bg: '#10b98120', color: '#10b981',  border: '#10b98140' },
  'Not Tested': { bg: '#64748b20', color: '#64748b', border: '#64748b40' },
};

const RedTeamFrameworkCard = ({
  framework,
  isCompliant,
  severity,
  nonCompliantPlugins = [],
  detectorNames = {},
  compliantCount = 0,
  totalCount = 0,
  idx = 0,
}) => {
    const barRef = useRef(null);
    const hasData = totalCount > 0;
    const badgeKey = !hasData ? 'Not Tested' : isCompliant ? 'Compliant' : severity;
    const sev = SEV_STYLES[badgeKey] || SEV_STYLES.Low;
    const pct = hasData ? Math.round(compliantCount / totalCount * 100) : 0;
    useEffect(() => {
    const raf = requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        if (barRef.current) barRef.current.style.width = `${pct}%`;
      })
    );
    return () => cancelAnimationFrame(raf);
  }, [pct]);

  return (
    <>
      <style>{styles}</style>
      <div
        className="bg-[#1e1e1e] border border-[#3c3c3c] rounded-xl p-4 flex flex-col gap-3"
        style={{ animation: `fadeUp 0.4s ease ${idx * 0.05}s both` }}
      >

        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-white font-mono">{framework}</h3>
          <div
            className="px-3 py-1 rounded-full text-xs font-bold tracking-widest uppercase"
            style={{ backgroundColor: sev.bg, color: sev.color, border: `1px solid ${sev.border}` }}
          >
            {badgeKey}
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-[3px] bg-[#3c3c3c] rounded-full overflow-hidden">
          <div
            ref={barRef}
            className="h-full rounded-full w-0"
            style={{
              backgroundColor: sev.color,
              transition: 'width 0.9s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
          />
        </div>

        {!hasData ? (
        <div className="flex items-center gap-2 py-2">
            <span className="text-sm font-mono text-slate-500">No test data available</span>
        </div>
        ) : isCompliant ? (
        <div className="flex items-center gap-2 py-2">
            <span
            className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: sev.bg, border: `1px solid ${sev.border}` }}
            >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M2 5l2.5 2.5L8 3" stroke="#10b981" strokeWidth="1.6"
                    strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            </span>
            <span className="text-sm font-mono" style={{ color: sev.color }}>
            All detectors passed
            </span>
        </div>
        ) : (
          <div className="flex flex-col gap-2">
            <p className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">
              Non-Compliant Detectors
            </p>
            <ul className="flex flex-col gap-1.5 max-h-44 overflow-y-auto">
              {nonCompliantPlugins.map((plugin, i) => {
                const pluginSev = plugin.severity ?? severity;
                const pluginId  = plugin.id ?? plugin;
                const pluginStyles = SEV_STYLES[pluginSev] || SEV_STYLES.Low;
                const name = detectorNames?.[pluginId] || pluginId;
                return (
                  <li
                    key={i}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#2a2a2a]"
                    style={{ animation: `slideIn 0.2s ease ${i * 0.04}s both` }}
                  >
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: pluginStyles.color }}
                    />
                    <span className="text-sm font-mono text-slate-200 flex-1 truncate">
                      {name}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-between text-xs font-mono text-slate-500 pt-1">
          <span>{compliantCount}/{totalCount} passed</span>
          <span>{pct}%</span>
        </div>

      </div>
    </>
  );
};

export default RedTeamFrameworkCard;